import { OpsApiError } from '../api/client.ts';

/**
 * The rated-anchor guards' payload, recovered from their message.
 *
 * `MODEL_PRICING_RATED_ANCHOR_CONFLICT` (platform `models/model-pricing.ts`)
 * and its unit twin `MODEL_UNIT_PRICING_RATED_ANCHOR_CONFLICT`
 * (`models/model-unit-pricing.ts`) carry the latest rated event instant only
 * INSIDE the message string — the error envelope has no structured details
 * member for it:
 *
 *   `... (latest rated event <ISO>); append a generation strictly after ...`
 *
 * The console translates that into an operator-readable sentence instead of
 * showing the raw code, on BOTH pricing forms. Returns the extracted ISO
 * instant, `null` for any other error, and `''` for a conflict whose instant
 * cannot be recovered (message shape drifted) — still a conflict, just
 * without the timestamp.
 */
const RATED_ANCHOR_CONFLICT_CODES: ReadonlySet<string> = new Set([
  'MODEL_PRICING_RATED_ANCHOR_CONFLICT',
  'MODEL_UNIT_PRICING_RATED_ANCHOR_CONFLICT',
]);

const RATED_ANCHOR_UNVERIFIABLE_CODES: ReadonlySet<string> = new Set([
  'MODEL_PRICING_RATED_ANCHOR_UNVERIFIABLE',
  'MODEL_UNIT_PRICING_RATED_ANCHOR_UNVERIFIABLE',
]);

/**
 * True when the guard refused because the rated history is bounded and the
 * requested effective time predates it. The message names the repair (pick a
 * time inside the retained window); the console should not print the raw code.
 */
export function ratedAnchorUnverifiable(err: unknown): boolean {
  return err instanceof OpsApiError && RATED_ANCHOR_UNVERIFIABLE_CODES.has(err.code);
}

export function ratedAnchorConflictLatestIso(err: unknown): string | null {
  if (!(err instanceof OpsApiError) || !RATED_ANCHOR_CONFLICT_CODES.has(err.code)) {
    return null;
  }
  const match = /latest rated event ([^);\s]+)\)/.exec(err.message);
  const iso = match?.[1];
  if (iso === undefined || !Number.isFinite(Date.parse(iso))) return '';
  return iso;
}

/**
 * The window-bounded readers' refusals. Each is a 409 with a distinct code;
 * the drawer/tab should explain the window instead of printing the envelope's
 * generic message. Kept here so the branches are unit-tested without a DOM.
 */
export function revenueWindowUnavailable(err: unknown): boolean {
  return err instanceof OpsApiError && err.code === 'OPS_REVENUE_WINDOW_UNAVAILABLE';
}

/**
 * A gateway usage_logs reader refused a window that reaches days the hot table
 * no longer holds (`USAGE_WINDOW_UNAVAILABLE`, `USAGE_SHORT_WINDOW_READS`).
 * The message already names the days; the copy tells the operator what to do.
 */
export function usageWindowUnavailable(err: unknown): boolean {
  return err instanceof OpsApiError && err.code === 'USAGE_WINDOW_UNAVAILABLE';
}

export const USAGE_WINDOW_UNAVAILABLE_HINT = '所选窗口包含已移出热表的日期，暂无对应汇总可答。缩短窗口到近几天内再试。';

export function noticeAudienceWindowUnavailable(err: unknown): boolean {
  return err instanceof OpsApiError && err.code === 'EMAIL_NOTICE_AUDIENCE_WINDOW_UNAVAILABLE';
}
